// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { IconButton, Tooltip, Box } from '@mui/material';
import { BiMessageDetail } from 'react-icons/bi';
import { useMoveChat } from '@features/moves/hooks/useMoveChat';
import { useChatModal } from '@components/Chat/ChatModalManager';
import { hasUnreadMessages, updateMoveReadStatus } from '@features/moves/services/smsReadStatusTracker';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

// TYPES ---------------------------------------------------------------- //

interface MoveChatBadgeProps {
  moveId?: number;
  consumerPickup?: boolean;
  variant?: 'icon' | 'button';
  size?: 'small' | 'medium' | 'large';
  showTooltip?: boolean;
}

// COMPONENT ---------------------------------------------------------------- //

const MoveChatBadge = ({ 
  moveId, 
  consumerPickup, 
  variant = 'icon',
  size = 'medium',
  showTooltip = true 
}: MoveChatBadgeProps) => {
  const isSelfManaged = useValetSelfManaged();
  const { messageCount, loading, messages } = useMoveChat(moveId, consumerPickup, {
    enabled: true,
    // No polling - global subscription handles updates
  });
  
  const { openChat } = useChatModal();
  const [isUnread, setIsUnread] = React.useState(false);

  // Check if there are unread messages
  React.useEffect(() => {
    if (moveId && messages.length > 0) {
      // Only update the status if we have messages, but don't reset read status
      const status = updateMoveReadStatus(moveId, messages);
      setIsUnread(status.hasUnread);
    } else if (moveId) {
      // If we have a moveId but no messages yet, check existing status
      setIsUnread(hasUnreadMessages(moveId));
    }
  }, [moveId, messages]);

  // Don't render for a non-self-managed customer, or without consumer pickup or a moveId.
  if (!isSelfManaged || !consumerPickup || !moveId) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
    openChat(moveId);
  };

  const getIconSize = () => {
    switch (size) {
      case 'small': return 18; // Better alignment with chips
      case 'large': return 28;
      default: return 24;
    }
  };


  const icon = <BiMessageDetail size={getIconSize()} />;

  const badge = (
    <Box sx={{ position: 'relative', display: 'inline-flex', zIndex: 2 }}>
      {variant === 'button' ? (
        <IconButton
          size={size}
          onClick={handleClick}
          sx={{
            color: 'inherit',
            position: 'relative',
            zIndex: 2,
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {icon}
        </IconButton>
      ) : (
        <Box
          onClick={handleClick}
          sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: size === 'small' ? '4px' : '6px',
            borderRadius: '50%', // Circular for better chip alignment
            transition: 'all 0.2s ease',
            minWidth: size === 'small' ? '28px' : '32px', // Match chip height better
            minHeight: size === 'small' ? '28px' : '32px',
            position: 'relative',
            zIndex: 2,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.08)',
              transform: 'scale(1.05)',
            },
          }}
        >
          {icon}
        </Box>
      )}
      {/* Red dot indicator for unread messages - moved to top right */}
      {isUnread && messageCount > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: variant === 'button' ? 0 : size === 'small' ? -1 : -2,
            right: variant === 'button' ? 0 : size === 'small' ? -1 : -2,
            width: size === 'small' ? 10 : 8,
            height: size === 'small' ? 10 : 8,
            borderRadius: '50%',
            backgroundColor: 'error.main',
            border: `2px solid white`,
            zIndex: 1,
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      )}
    </Box>
  );

  if (!showTooltip) {
    return badge;
  }

  const tooltipTitle = loading 
    ? 'Loading messages...' 
    : messageCount > 0 
      ? isUnread 
        ? `${messageCount} SMS message${messageCount !== 1 ? 's' : ''} (unread)`
        : `${messageCount} SMS message${messageCount !== 1 ? 's' : ''}`
      : 'No SMS messages';

  return (
    <Tooltip title={tooltipTitle} placement="top">
      {badge}
    </Tooltip>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default MoveChatBadge;